import { sampleData } from '../../../../../DummyData/ConvertSampleJson';
import React, { useState } from 'react';

// Define types for our component
interface JsonToJavaProps {}

// Java generation options
type JavaOptions = {
  packageName: string;
  useLombok: boolean;
  generateGettersSetters: boolean;
  useJacksonAnnotations: boolean;
  generateToString: boolean;
  generateEqualsHashCode: boolean;
  generateBuilder: boolean;
  indentationSpaces: number;
  useJavaRecords: boolean;
  accessModifier: 'public' | 'private' | 'protected' | 'default';
}

// Support for different naming conventions
const toPascalCase = (str: string): string => {
  if (!str) return '';
  return str
    .replace(/[-_](\w)/g, (_, c) => c.toUpperCase())
    .replace(/^\w/, c => c.toUpperCase());
};

const toCamelCase = (str: string): string => {
  if (!str) return '';
  const pascal = toPascalCase(str);
  return pascal.charAt(0).toLowerCase() + pascal.slice(1);
};

const JsonToJava: React.FC<JsonToJavaProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the Java output
  const [javaOutput, setJavaOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the root class name
  const [rootClassName, setRootClassName] = useState<string>('RootClass');
  // State for the package name
  const [packageName, setPackageName] = useState<string>('com.example.model');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State to track if the conversion happened
  const [isCodeGenerated, setIsCodeGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for Java options
  const [javaOptions, setJavaOptions] = useState<JavaOptions>({
    packageName: 'com.example.model',
    useLombok: true,
    generateGettersSetters: false,
    useJacksonAnnotations: true,
    generateToString: false,
    generateEqualsHashCode: false,
    generateBuilder: false,
    indentationSpaces: 4,
    useJavaRecords: false,
    accessModifier: 'private'
  });



  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsCodeGenerated(false);
  };

  // Handle root class name change
  const handleRootClassNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRootClassName(e.target.value);
  };

  // Handle package name change
  const handlePackageNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPackageName(e.target.value);
    setJavaOptions({...javaOptions, packageName: e.target.value});
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setError('');
    setIsCodeGenerated(false);
    setJavaOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setJavaOutput('');
    setError('');
    setRootClassName('RootClass');
    setPackageName('com.example.model');
    setJavaOptions({
      packageName: 'com.example.model',
      useLombok: true,
      generateGettersSetters: false,
      useJacksonAnnotations: true,
      generateToString: false,
      generateEqualsHashCode: false,
      generateBuilder: false,
      indentationSpaces: 4,
      useJavaRecords: false,
      accessModifier: 'private'
    });
    setIsCodeGenerated(false);
  };

  // Handle change in Java options
  const handleOptionChange = (option: keyof JavaOptions, value: any) => {
    setJavaOptions({...javaOptions, [option]: value});
  };

  // Get Java type for a JavaScript value
  const getJavaType = (value: any, isArray = false): string => {
    if (value === null || value === undefined) return isArray ? "List<Object>" : "Object";
    
    const type = typeof value;
    
    switch (type) {
      case 'string':
        return isArray ? "List<String>" : "String";
      case 'number':
        // Check if the number is an integer
        return isArray 
          ? Number.isInteger(value) ? "List<Integer>" : "List<Double>" 
          : Number.isInteger(value) ? "int" : "double";
      case 'boolean':
        return isArray ? "List<Boolean>" : "boolean";
      case 'object':
        if (Array.isArray(value)) {
          if (value.length === 0) return "List<Object>";
          return `List<${getJavaType(value[0])}>`;
        }
        return isArray ? `List<${getClassName(value)}>` : getClassName(value);
      default:
        return isArray ? "List<Object>" : "Object";
    }
  };

  // Get a class name for an object
  const getClassName = (obj: any): string => {
    if (!obj) return "Object";
    
    // Try to infer a meaningful name from object properties
    if (obj.name && typeof obj.name === 'string') {
      return toPascalCase(obj.name);
    }
    if (obj.type && typeof obj.type === 'string') {
      return toPascalCase(obj.type);
    }
    if (obj.title && typeof obj.title === 'string') {
      return toPascalCase(obj.title);
    }
    
    // Default to a generic name
    return "CustomObject";
  };

  // Generate indentation
  const getIndent = (level: number): string => {
    return ' '.repeat(javaOptions.indentationSpaces * level);
  };

  // Generate Java field declaration
  const generateField = (
    name: string,
    type: string,
    indentLevel: number,
    useJsonProperty: boolean
  ): string => {
    const indent = getIndent(indentLevel);
    const fieldName = toCamelCase(name);
    
    let result = '';
    
    // Add Jackson annotation if requested
    if (useJsonProperty && javaOptions.useJacksonAnnotations) {
      result += `${indent}@JsonProperty("${name}")\n`;
    }
    
    // Add field declaration
    result += `${indent}${javaOptions.accessModifier} ${type} ${fieldName};`;
    
    return result;
  };

  // Generate Java getters and setters
  const generateGetterSetter = (name: string, type: string, indentLevel: number): string => {
    const indent = getIndent(indentLevel);
    const fieldName = toCamelCase(name);
    const capitalizedName = toPascalCase(name);
    
    let result = '';
    
    // Getter
    result += `${indent}public ${type} get${capitalizedName}() {\n`;
    result += `${indent}${getIndent(1)}return ${fieldName};\n`;
    result += `${indent}}\n\n`;
    
    // Setter
    result += `${indent}public void set${capitalizedName}(${type} ${fieldName}) {\n`;
    result += `${indent}${getIndent(1)}this.${fieldName} = ${fieldName};\n`;
    result += `${indent}}`;
    
    return result;
  };

  // Generate Java toString method
  const generateToStringMethod = (className: string, fields: string[], indentLevel: number): string => {
    const indent = getIndent(indentLevel);
    
    let result = `${indent}@Override\n`;
    result += `${indent}public String toString() {\n`;
    result += `${indent}${getIndent(1)}return "${className}{" +\n`;
    
    fields.forEach((field, index) => {
      const fieldName = toCamelCase(field);
      if (index === 0) {
        result += `${indent}${getIndent(2)}"${field}=" + ${fieldName} +\n`;
      } else {
        result += `${indent}${getIndent(2)}", ${field}=" + ${fieldName} +\n`;
      }
    });
    
    result += `${indent}${getIndent(2)}"}";\n`;
    result += `${indent}}`;
    
    return result;
  };

  // Generate Java equals and hashCode methods
  const generateEqualsHashCode = (className: string, fields: string[], indentLevel: number): string => {
    const indent = getIndent(indentLevel);
    
    let result = `${indent}@Override\n`;
    result += `${indent}public boolean equals(Object o) {\n`;
    result += `${indent}${getIndent(1)}if (this == o) return true;\n`;
    result += `${indent}${getIndent(1)}if (o == null || getClass() != o.getClass()) return false;\n`;
    result += `${indent}${getIndent(1)}${className} that = (${className}) o;\n`;
    
    if (fields.length > 0) {
      result += `${indent}${getIndent(1)}return `;
      
      fields.forEach((field, index) => {
        const fieldName = toCamelCase(field);
        if (index > 0) {
          result += `${indent}${getIndent(3)}&& `;
        }
        result += `Objects.equals(${fieldName}, that.${fieldName})`;
        if (index < fields.length - 1) {
          result += `\n`;
        }
      });
      
      result += `;\n`;
    } else {
      result += `${indent}${getIndent(1)}return true;\n`;
    }
    
    result += `${indent}}\n\n`;
    
    // HashCode
    result += `${indent}@Override\n`;
    result += `${indent}public int hashCode() {\n`;
    
    if (fields.length > 0) {
      const camelCaseFields = fields.map(field => toCamelCase(field));
      result += `${indent}${getIndent(1)}return Objects.hash(${camelCaseFields.join(', ')});\n`;
    } else {
      result += `${indent}${getIndent(1)}return 0;\n`;
    }
    
    result += `${indent}}`;
    
    return result;
  };

  // Generate Java builder pattern
  const generateBuilderPattern = (className: string, fields: string[], types: {[key: string]: string}, indentLevel: number): string => {
    const indent = getIndent(indentLevel);
    
    let result = `${indent}public static class Builder {\n`;
    
    // Private fields in builder
    fields.forEach(field => {
      const fieldName = toCamelCase(field);
      const fieldType = types[field];
      result += `${indent}${getIndent(1)}private ${fieldType} ${fieldName};\n`;
    });
    
    result += '\n';
    
    // Builder methods
    fields.forEach(field => {
      const fieldName = toCamelCase(field);
      const capitalizedName = toPascalCase(field);
      const fieldType = types[field];
      
      result += `${indent}${getIndent(1)}public Builder ${fieldName}(${fieldType} ${fieldName}) {\n`;
      result += `${indent}${getIndent(2)}this.${fieldName} = ${fieldName};\n`;
      result += `${indent}${getIndent(2)}return this;\n`;
      result += `${indent}${getIndent(1)}}\n\n`;
    });
    
    // Build method
    result += `${indent}${getIndent(1)}public ${className} build() {\n`;
    result += `${indent}${getIndent(2)}${className} ${toCamelCase(className)} = new ${className}();\n`;
    
    fields.forEach(field => {
      const fieldName = toCamelCase(field);
      result += `${indent}${getIndent(2)}${toCamelCase(className)}.${fieldName} = this.${fieldName};\n`;
    });
    
    result += `${indent}${getIndent(2)}return ${toCamelCase(className)};\n`;
    result += `${indent}${getIndent(1)}}\n`;
    result += `${indent}}`;
    
    return result;
  };

  // Generate Java record
  const generateJavaRecord = (
    obj: any,
    className: string,
    indentLevel: number,
    processedClasses: Set<string>,
    classDefinitions: string[] = []
  ): string => {
    const indent = getIndent(indentLevel);
    let result = '';
    
    // Add Jackson annotation if requested
    if (javaOptions.useJacksonAnnotations) {
      result += `${indent}@JsonIgnoreProperties(ignoreUnknown = true)\n`;
    }
    
    // Start record declaration
    result += `${indent}public record ${className}(`;
    
    // Add record parameters
    const keys = Object.keys(obj);
    const params: string[] = [];
    
    for (const key of keys) {
      const value = obj[key];
      let type = getJavaType(value);
      
      // For objects, we need to create a nested record
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const nestedClassName = toPascalCase(key);
        
        // Generate nested record if not processed already
        if (!processedClasses.has(nestedClassName)) {
          generateJavaRecord(value, nestedClassName, indentLevel, processedClasses, classDefinitions);
        }
      } 
      // For arrays with object elements, create a nested record for the elements
      else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
        const itemClassName = toPascalCase(key.replace(/s$/, ''));  // Singular form for item type
        
        // Generate nested record if not processed already
        if (!processedClasses.has(itemClassName)) {
          generateJavaRecord(value[0], itemClassName, indentLevel, processedClasses, classDefinitions);
        }
      }
      
      // Add Jackson annotation if requested
      if (javaOptions.useJacksonAnnotations) {
        params.push(`@JsonProperty("${key}") ${type} ${toCamelCase(key)}`);
      } else {
        params.push(`${type} ${toCamelCase(key)}`);
      }
    }
    
    result += params.join(', ');
    result += ') {}';
    
    // Add record to class definitions
    classDefinitions.push(result);
    
    return classDefinitions.join('\n\n');
  };

  // Generate Java class
  const generateClass = (
    obj: any,
    className: string,
    indentLevel: number,
    processedClasses: Set<string>,
    classDefinitions: string[] = []
  ): string => {
    if (processedClasses.has(className)) {
      return ''; // Already processed this class
    }
    
    processedClasses.add(className);
    
    const indent = getIndent(indentLevel);
    let result = '';
    
    // Add Lombok annotations if requested
    if (javaOptions.useLombok) {
      result += `${indent}@Data\n`;
      if (javaOptions.generateBuilder) {
        result += `${indent}@Builder\n`;
      }
      if (javaOptions.generateToString) {
        result += `${indent}@ToString\n`;
      }
      if (javaOptions.generateEqualsHashCode) {
        result += `${indent}@EqualsAndHashCode\n`;
      }
      result += `${indent}@NoArgsConstructor\n`;
      result += `${indent}@AllArgsConstructor\n`;
    }
    
    // Add Jackson annotation if requested
    if (javaOptions.useJacksonAnnotations) {
      result += `${indent}@JsonIgnoreProperties(ignoreUnknown = true)\n`;
    }
    
    // Class declaration
    result += `${indent}public class ${className} {\n\n`;
    
    // Add fields and track their types
    const keys = Object.keys(obj);
    const fields: string[] = [];
    const fieldTypes: {[key: string]: string} = {};
    
    for (const key of keys) {
      const value = obj[key];
      let type = getJavaType(value);
      
      // For arrays, check the type of elements
      if (Array.isArray(value)) {
        if (value.length > 0) {
          const firstItem = value[0];
          
          // If it's an object, create a nested class
          if (typeof firstItem === 'object' && firstItem !== null && !Array.isArray(firstItem)) {
            const itemClassName = toPascalCase(key.replace(/s$/, ''));  // Singular form for item type
            
            // Generate nested class
            generateClass(firstItem, itemClassName, indentLevel, processedClasses, classDefinitions);
            
            type = `List<${itemClassName}>`;
          }
        }
      } 
      // For objects, create a nested class
      else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const nestedClassName = toPascalCase(key);
        
        // Generate nested class
        generateClass(value, nestedClassName, indentLevel, processedClasses, classDefinitions);
        
        type = nestedClassName;
      }
      
      fields.push(key);
      fieldTypes[key] = type;
      
      // Add field
      result += generateField(key, type, indentLevel + 1, true) + '\n\n';
    }
    
    // Add getters and setters if requested and not using Lombok
    if (javaOptions.generateGettersSetters && !javaOptions.useLombok) {
      result += '\n';
      for (const key of keys) {
        result += generateGetterSetter(key, fieldTypes[key], indentLevel + 1) + '\n\n';
      }
    }
    
    // Add toString if requested and not using Lombok
    if (javaOptions.generateToString && !javaOptions.useLombok) {
      result += '\n';
      result += generateToStringMethod(className, fields, indentLevel + 1) + '\n\n';
    }
    
    // Add equals and hashCode if requested and not using Lombok
    if (javaOptions.generateEqualsHashCode && !javaOptions.useLombok) {
      result += '\n';
      result += generateEqualsHashCode(className, fields, indentLevel + 1) + '\n\n';
    }
    
    // Add builder pattern if requested and not using Lombok
    if (javaOptions.generateBuilder && !javaOptions.useLombok) {
      result += '\n';
      result += generateBuilderPattern(className, fields, fieldTypes, indentLevel + 1) + '\n';
    }
    
    result += `${indent}}`;
    
    // Add class to definitions
    classDefinitions.push(result);
    
    return classDefinitions.join('\n\n');
  };

  // Generate full Java code with imports and package
  const generateFullJavaCode = (jsonObj: any): string => {
    const processedClasses = new Set<string>();
    const classDefinitions: string[] = [];
    
    // Generate code based on the option
    if (javaOptions.useJavaRecords) {
      generateJavaRecord(jsonObj, rootClassName, 0, processedClasses, classDefinitions);
    } else {
      generateClass(jsonObj, rootClassName, 0, processedClasses, classDefinitions);
    }
    
    // Build the full Java code with imports
    let fullCode = `package ${javaOptions.packageName};\n\n`;
    
    // Add imports
    fullCode += 'import java.util.List;\n';
    fullCode += 'import java.util.Objects;\n';
    
    if (javaOptions.useJacksonAnnotations) {
      fullCode += 'import com.fasterxml.jackson.annotation.JsonIgnoreProperties;\n';
      fullCode += 'import com.fasterxml.jackson.annotation.JsonProperty;\n';
    }
    
    if (javaOptions.useLombok) {
      fullCode += 'import lombok.AllArgsConstructor;\n';
      fullCode += 'import lombok.Data;\n';
      
      if (javaOptions.generateBuilder) {
        fullCode += 'import lombok.Builder;\n';
      }
      
      if (javaOptions.generateToString) {
        fullCode += 'import lombok.ToString;\n';
      }
      
      if (javaOptions.generateEqualsHashCode) {
        fullCode += 'import lombok.EqualsAndHashCode;\n';
      }
      
      fullCode += 'import lombok.NoArgsConstructor;\n';
    }
    
    fullCode += '\n';
    fullCode += classDefinitions.join('\n\n');
    
    return fullCode;
  };

  // Convert JSON to Java classes
  const convertJsonToJava = () => {
    setError('');
    setIsLoading(true);
    setIsCodeGenerated(false);
    setJavaOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      const javaCode = generateFullJavaCode(jsonData);
      
      setIsCodeGenerated(true);
      setJavaOutput(javaCode);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating Java code: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy Java code to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(javaOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download Java file
  const downloadJava = () => {
    const blob = new Blob([javaOutput], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${rootClassName}.java`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to Java Class Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to Java classes. Paste your JSON data below to generate strongly-typed Java class definitions with optional Lombok support.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="rootClassName" className="block text-sm font-medium text-gray-700 mb-1">
                Root Class Name
              </label>
              <input
                type="text"
                id="rootClassName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={rootClassName}
                onChange={handleRootClassNameChange}
                placeholder="Enter root class name"
              />
            </div>
            
            <div className="mb-4">
              <label htmlFor="packageName" className="block text-sm font-medium text-gray-700 mb-1">
                Package Name
              </label>
              <input
                type="text"
                id="packageName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={packageName}
                onChange={handlePackageNameChange}
                placeholder="Enter package name"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="useLombok"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={javaOptions.useLombok}
                    onChange={(e) => handleOptionChange('useLombok', e.target.checked)}
                  />
                  <label htmlFor="useLombok" className="ml-2 block text-sm text-gray-700">
                    Use Lombok (@Data, @NoArgsConstructor, etc.)
                  </label>
                </div>
                
                {!javaOptions.useLombok && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="generateGettersSetters"
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      checked={javaOptions.generateGettersSetters}
                      onChange={(e) => handleOptionChange('generateGettersSetters', e.target.checked)}
                    />
                    <label htmlFor="generateGettersSetters" className="ml-2 block text-sm text-gray-700">
                      Generate Getters and Setters
                    </label>
                  </div>
                )}
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="useJacksonAnnotations"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={javaOptions.useJacksonAnnotations}
                    onChange={(e) => handleOptionChange('useJacksonAnnotations', e.target.checked)}
                  />
                  <label htmlFor="useJacksonAnnotations" className="ml-2 block text-sm text-gray-700">
                    Use Jackson Annotations (@JsonProperty)
                  </label>
                </div>
                
                {!javaOptions.useLombok && (
                  <>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="generateToString"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        checked={javaOptions.generateToString}
                        onChange={(e) => handleOptionChange('generateToString', e.target.checked)}
                      />
                      <label htmlFor="generateToString" className="ml-2 block text-sm text-gray-700">
                        Generate toString() Method
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="generateEqualsHashCode"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        checked={javaOptions.generateEqualsHashCode}
                        onChange={(e) => handleOptionChange('generateEqualsHashCode', e.target.checked)}
                      />
                      <label htmlFor="generateEqualsHashCode" className="ml-2 block text-sm text-gray-700">
                        Generate equals() and hashCode() Methods
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="generateBuilder"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        checked={javaOptions.generateBuilder}
                        onChange={(e) => handleOptionChange('generateBuilder', e.target.checked)}
                      />
                      <label htmlFor="generateBuilder" className="ml-2 block text-sm text-gray-700">
                        Generate Builder Pattern
                      </label>
                    </div>
                  </>
                )}
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="useJavaRecords"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={javaOptions.useJavaRecords}
                    onChange={(e) => handleOptionChange('useJavaRecords', e.target.checked)}
                  />
                  <label htmlFor="useJavaRecords" className="ml-2 block text-sm text-gray-700">
                    Use Java Records (Java 16+)
                  </label>
                </div>
                
                <div>
                  <label htmlFor="accessModifier" className="block text-sm font-medium text-gray-700 mb-1">
                    Field Access Modifier
                  </label>
                  <select
                    id="accessModifier"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={javaOptions.accessModifier}
                    onChange={(e) => handleOptionChange('accessModifier', e.target.value)}
                    disabled={javaOptions.useJavaRecords}
                  >
                    <option value="private">private</option>
                    <option value="public">public</option>
                    <option value="protected">protected</option>
                    <option value="default">default (package-private)</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="indentationSpaces" className="block text-sm font-medium text-gray-700 mb-1">
                    Indentation Spaces
                  </label>
                  <input
                    type="number"
                    id="indentationSpaces"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={javaOptions.indentationSpaces}
                    onChange={(e) => handleOptionChange('indentationSpaces', parseInt(e.target.value) || 4)}
                    min={1}
                    max={8}
                  />
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToJava}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to Java'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              Java Output
            </label>
            
            {isCodeGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy Code'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadJava}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isCodeGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  <p>
                    Java code will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isCodeGenerated && (
              <div className="flex-grow flex flex-col">
                <div className="flex-grow flex flex-col">
                  <textarea
                    id="javaOutput"
                    className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                    value={javaOutput}
                    readOnly
                  />
                </div>
              </div>
            )}
          </div>
          
          {isCodeGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">Java Classes Successfully Generated!</p>
              </div>
              <p className="text-sm">
                {javaOptions.useLombok 
                  ? "The generated code uses Lombok annotations to reduce boilerplate. Make sure to have Lombok configured in your project."
                  : "The generated code includes standard Java classes with all necessary methods and fields."}
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to Java Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool analyzes your JSON data structure and generates corresponding Java classes. It automatically 
              determines appropriate types, creates nested classes for complex objects, and maintains the 
              relationships between your data models.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Type Inference</strong> - Automatically determines Java types from JSON values</li>
              <li><strong>Nested Classes</strong> - Creates separate classes for complex nested objects</li>
              <li><strong>Collection Support</strong> - Properly handles arrays as List&lt;T&gt; types</li>
              <li><strong>Jackson Integration</strong> - Adds Jackson annotations for proper serialization</li>
              <li><strong>Lombok Support</strong> - Option to use Lombok annotations to reduce boilerplate code</li>
              <li><strong>Java Records</strong> - Option to generate modern Java record classes (Java 16+)</li>
              <li><strong>Builder Pattern</strong> - Option to include builder pattern implementation</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format JSON" button to properly format and validate your JSON data before conversion</li>
              <li>Give a meaningful name to your root class that reflects the data it represents</li>
              <li>For arrays, the converter will try to infer the type from the first element, so ensure your sample JSON includes representative data</li>
              <li>Enable Lombok for cleaner code if your project supports it</li>
              <li>Consider using Java Records for simple data classes if targeting Java 16 or newer</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Java Code Usage</h3>
            <p className="text-gray-600">
              Once you've generated your Java classes, you can use them with Jackson for serialization/deserialization:
            </p>
            <div className="mt-2 bg-gray-100 p-3 rounded-md">
              <code className="text-sm font-mono">
                // Deserialize JSON to Java objects<br/>
                ObjectMapper mapper = new ObjectMapper();<br/>
                String jsonString = "your JSON string here";<br/>
                RootClass data = mapper.readValue(jsonString, RootClass.class);<br/>
                <br/>
                // Access properties<br/>
                System.out.println(data.getPropertyName());<br/>
                <br/>
                // Serialize Java objects back to JSON<br/>
                String newJsonString = mapper.writeValueAsString(data);
              </code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToJava;